import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  ContainerErrorPageStyled,
  ContainerTitleStyled,
  ContainerButton,
  IconStyled,
  getCommonTitleSx,
  getSubtitleSx,
  getErrorButtonSx,
} from './styles/ErrorPageStyles';
import AtomButton from '../../components/atoms/AtomButton/AtomButton';
import AtomTypography from '../../components/atoms/AtomTypography/AtomTypography';
import Page from '../../components/template-minimal/Page';
import userAuth from '../../hooks/userAuth';
import { PATH_AUTH, PATH_DASHBOARD, PATH_DASHBOARD_STUDENT } from '../../routes/paths';
import { ROLE_ENUM } from '../Auth/Login/interfaces/IGoogleUser';

interface IErrorPageProps {
  translationKey: string;
  iconSrc?: string;
  iconComponent?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  pageTitle?: string;
  showBoldTitle?: boolean;
  boldTitleText?: string;
  titleKey?: string;
  subtitleKey?: string;
  buttonKey?: string;
}

const ErrorPage: React.FC<IErrorPageProps> = ({
  translationKey,
  iconSrc,
  iconComponent: IconComponent,
  pageTitle,
  showBoldTitle = false,
  boldTitleText,
  titleKey = 'TITLE',
  subtitleKey = 'SUBTITLE',
  buttonKey = 'HOME_BUTTON',
}) => {
  const { t } = useTranslation(translationKey);
  const theme = useTheme();
  const { auth } = userAuth();
  const navigate = useNavigate();
  const isLearner: boolean = auth?.userRole === ROLE_ENUM.STUDENT;
  const isAdvisor: boolean = auth?.userRole === ROLE_ENUM.ADMIN;

  const goHomeClick = (): void => {
    if (isLearner) {
      navigate(PATH_DASHBOARD_STUDENT.admin.pageHome);
    } else if (isAdvisor) {
      navigate(PATH_DASHBOARD.admin.pageInsights);
    } else {
      navigate(PATH_AUTH.login);
    }
  };

  return (
    <Page title={pageTitle || t(titleKey)}>
      <ContainerErrorPageStyled>
        {IconComponent ? (
          <IconComponent
            style={{
              height: '200px',
              width: '200px',
              marginBottom: '32px',
            }}
          />
        ) : (
          iconSrc && <IconStyled src={iconSrc} />
        )}
        <ContainerTitleStyled>
          {showBoldTitle && (
            <AtomTypography fontWeight={700} sx={getCommonTitleSx(theme)}>
              {boldTitleText}
            </AtomTypography>
          )}
          <AtomTypography
            fontWeight={400}
            sx={{
              ...getCommonTitleSx(theme),
              textAlign: 'center',
            }}
          >
            {t(titleKey)}
          </AtomTypography>
        </ContainerTitleStyled>
        <AtomTypography fontWeight={700} sx={getSubtitleSx(theme)}>
          {t(subtitleKey)}
        </AtomTypography>
        <ContainerButton>
          <AtomButton onClick={goHomeClick} sx={getErrorButtonSx(theme)}>
            {t(buttonKey)}
          </AtomButton>
        </ContainerButton>
      </ContainerErrorPageStyled>
    </Page>
  );
};

export default ErrorPage;
